import React, { PropsWithChildren } from 'react';
import { useQuery } from '@apollo/client';
import { AppContextQuery } from './graphqlTypes/AppContextQuery';
import appContextQuery from './AppContext.gql';
import { PageError } from '~/components/PageError';

const defaultAppContext: AppContextQuery = {
  me: {
    __typename: 'User',
    id: 0,
    firstName: '',
    lastName: '',
    emailAddress: '',
    permissions: [],
    role: null,
  },
  applications: [],
  backend: {
    __typename: 'BackendInfo',
    featureBranch: null,
    isProduction: true,
    isLocal: false,
    version: '',
  },
  streamingRights: [],
  countries: [],
};

/**
 * Context for the entire ops-portal app. This is a container for anything that is globally useful.
 */
export const AppContext = React.createContext<AppContextQuery>( defaultAppContext );

/**
 * Loads application-level data from GraphQL and provides it downward using `AppContext`. Consume
 * any of this data using `useContext( AppContext )`.
 */
export function AppContextProvider( props: PropsWithChildren<{}> ) {
  const { data, error } = useQuery<AppContextQuery>( appContextQuery );

  if ( error ) {
    console.error( 'AppContextProvider received an error from Apollo: %o', error );
    return <PageError summary="Environment error" error={ error } />;
  }

  if ( data ) {
    return <AppContext.Provider value={ data }>{ props.children }</AppContext.Provider>;
  }

  return null;
}
